/**
 * Import Progress Indicator — a FAILED import.
 *
 * Renders a failed background import as an explicit ERROR state (danger ring,
 * localized "Import failed" title + the SAFE backend failure message). This
 * exists so a failed import is NEVER shown as a green "success" bar — the user
 * must clearly see that the import failed / lost rows.
 *
 * `message` is the safe, category-mapped string produced server-side
 * (import-failure-message.ts). It never contains raw SQL / PII. When the
 * backend shipped no message, the caller substitutes a localized generic
 * fallback before constructing this entry.
 *
 * Retry is offered only when the job belongs to a source (a CSV upload has none
 * to restart: its file is re-uploaded from the importer).
 */

import { useState } from 'react'

import { Loader2, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { resolveSyncSourceLabel } from '@/platform/sync/sync-source-label'

import { ImportItemHeader } from './ImportItemHeader'
import { FOCUS_RING_CLASS, VenueTile } from './ImportRowParts'

export interface FailedImport {
  jobId: string
  /** The source the job imported, when it has one — what a retry restarts. */
  sourceId?: string | null
  sourceName: string
  failedAt: Date
  message: string
}

interface ImportFailedItemProps {
  entry: FailedImport
  onDismiss: () => void
  formatRelativeTime: (date: Date) => string
  /** Restart the source's import. Rejects when the server refused the restart. */
  onRetry?: () => Promise<void>
}

export const ImportFailedItem = ({
  entry,
  onDismiss,
  formatRelativeTime,
  onRetry,
}: ImportFailedItemProps) => {
  const { t } = useTranslation('imports')
  const { t: tCommon } = useTranslation('common')
  const [retry, setRetry] = useState<'idle' | 'retrying' | 'refused'>('idle')

  const handleRetry = async () => {
    if (!onRetry || retry === 'retrying') return
    setRetry('retrying')
    try {
      await onRetry()
      setRetry('idle')
    } catch {
      setRetry('refused')
    }
  }

  return (
    <div
      className="rounded-lg border border-crypto-danger/40 bg-crypto-danger/10 px-3 py-2.5 space-y-1.5"
      data-testid={`failed-import-item-${entry.jobId}`}
      role="alert"
    >
      <ImportItemHeader
        icon={
          <VenueTile label={resolveSyncSourceLabel(entry.sourceName, tCommon)} state="failed" />
        }
        sourceName={entry.sourceName}
        timestampLabel={formatRelativeTime(entry.failedAt)}
        onDismiss={onDismiss}
        dismissTestId={`failed-import-dismiss-${entry.jobId}`}
        dismissHoverClassName="hover:bg-crypto-danger/20"
      />

      <div className="pl-10 space-y-1">
        <p className="text-xs font-semibold text-crypto-danger" data-testid="failed-import-title">
          {t('progressIndicator.failed.title', 'Import failed')}
        </p>
        <p className="text-xs text-crypto-text break-words" data-testid="failed-import-message">
          {entry.message}
        </p>
        {retry === 'refused' && (
          <p className="text-xs text-crypto-danger" data-testid={`failed-import-retry-error-${entry.jobId}`}>
            {t('progressIndicator.retryRefused', 'The import could not be restarted. Try again in a few seconds.')}
          </p>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={() => void handleRetry()}
            disabled={retry === 'retrying'}
            className={`mt-1 inline-flex items-center gap-1.5 rounded-md border border-crypto-danger/40 px-2.5 py-1 text-xs font-medium text-crypto-text hover:bg-crypto-danger/20 disabled:opacity-60 disabled:cursor-wait ${FOCUS_RING_CLASS}`}
            data-testid={`failed-import-retry-${entry.jobId}`}
          >
            {retry === 'retrying' ? (
              <Loader2 className="w-3.5 h-3.5 motion-safe:animate-spin" aria-hidden="true" />
            ) : (
              <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
            )}
            {retry === 'retrying'
              ? t('progressIndicator.retrying', 'Restarting…')
              : t('progressIndicator.retry', 'Retry')}
          </button>
        )}
      </div>
    </div>
  )
}
